import React, { useState } from 'react'

import { isValid, parse } from 'date-fns'
import { Header } from './components/Header'
import { Body } from './components/Body'
import { useSorting } from './hooks/useSorting'

import { TableContainerStyle, TableStyle, LoadingContainer, ErrorStyle } from './styles'
import { IDataModel, ITableProps } from './types'
import { SpinnerIconLoader } from '../spinnerIconLoader'
import { DataNotFound } from '../DataNotFound'
import { IconComponent } from '~/assets/icons/component'

/**
 * Este componente irá renderizar uma Tabela
 * @param {modelItem[]} model É um array de objetos que configura as colunas e o tamanho delas.
 * @param {modelItem[]} modelScroll (opcional) É um array de objetos que configura as colunas e o tamanho delas, dentro de uma div com scroll.
 * @param {DataModel[]} data É um array de objetos com os dados da tabela que pode ser extendido da interface IDataModel.
 * @param {DataModel[]} dataScroll É um array de objetos com os dados da tabela, dentro de uma div com scroll.
 *
 * @param {string} lineHeight Dita a altura da linha
 * @param {string} maxHeight Dita se altura da tabela tem limite
 * @param {string} headerLineHeight Dita a altura do header da tabela
 * @param {string} headerPadding Dita o espaçamento total do header
 * @param {string} headerAlign Dita o alinhamento dos items internos perante ao header
 * @param {string} contentBackground Dita qual será o background do corpo da tabela
 * @param {number} totalPages Dita quantas páginas a tabela terá. Habilita a paginação
 * @param {boolean} noHover Desabilita o efeito de Hover nas linhas da tabela
 * @param {boolean | undefined} isLoading Exibe o componente de carregamento caso seja true
 * @param {any} apiCall Recebe uma chamada de api para ser executada ao mudar de página (necessário ter mais de 1 página em totalPages)
 * @param {boolean} errorApi Caso true, exibe a mensagem de erro
 * @param {string | ReactElement<any>} messageError Define uma mensagem de erro customizada, podendo ser um texto ou um component React
 * @param {boolean} Pagination Define se a paginação da tabela será no modelo antigo ou no novo.
 * @param {boolean} mountApiCall Faz a chamada na API na primeira renderização do componente
 * @param {boolean} apiCallByFunction Chamada da API passa a ser via função ao invés de useEffect, somente se utilizando Pagination
 * @param {boolean} resetPage Se true, volta o contador da paginação para o valor 1, somente se utilizando Pagination
 */
const Table = ({
  model,
  modelScroll,
  data,
  dataScroll,
  lineHeight,
  noHover,
  apiCall,
  isLoading,
  initialPage = '1',
  errorAPI,
  messageError,
  headerLineHeight,
  headerPadding,
  headerAlign,
  contentBackground,
  mountApiCall,
  rowSeparator,
  minHeight,
  maxHeight,
  onRefresh
}: ITableProps): JSX.Element => {
  const errorMessage = messageError ?? 'Nenhum dado encontrado'
  const { selectedColumn, isDesc, onSorting } = useSorting()
  const [sortedData, setSortedData] = useState<IDataModel[]>(data)

  React.useLayoutEffect(() => {
    if (mountApiCall) {
      apiCall?.(initialPage)
    }
  }, [])

  React.useEffect(() => {
    if (data) setSortedData(data)
  }, [data])

  const checkType = (data: string): string | number => {
    const parsedDate = parse(data, 'dd/MM/yyyy', new Date())
    const isValidDate = isValid(parsedDate)

    if (isValidDate) {
      const timestamp = parsedDate.getTime()
      return timestamp
    }

    return data
  }

  const sortData = (data: any[], columnKey: string, isDesc: boolean) => {
    return data.sort((a, b) => {
      return checkType(a[columnKey]) < checkType(b[columnKey]) ? (isDesc ? 1 : -1) : isDesc ? -1 : 1
    })
  }

  const handleSort = (columnKey: string) => {
    onSorting(columnKey)
    const sorted = sortData(data, columnKey, isDesc)
    setSortedData(sorted)
  }

  return (
    <TableContainerStyle minHeight={minHeight}>
      {/* <TableStyle styleScroll={dataScroll && dataScroll?.length > 0}> */}
      <Header
        model={model}
        lineHeight={headerLineHeight}
        padding={headerPadding}
        alignItems={headerAlign}
        sorting={{
          isDesc,
          selectedColumn,
          onSorting: handleSort
        }}
      />
      <Body
        maxHeight={maxHeight}
        background={contentBackground}
        model={model}
        isLoading={isLoading ?? false}
        data={sortedData}
        lineHeight={lineHeight}
        noHover={noHover}
        rowSeparator={rowSeparator}
      />
      {modelScroll && modelScroll.length > 0 && (
        <TableStyle scroll>
          <Header model={modelScroll} lineHeight={headerLineHeight} padding={headerPadding} alignItems={headerAlign} />
          {dataScroll && dataScroll.length > 0 && (
            <Body
              background={contentBackground}
              model={modelScroll}
              isLoading={isLoading ?? false}
              data={dataScroll}
              lineHeight={lineHeight}
              noHover={noHover}
              rowSeparator={rowSeparator}
            />
          )}
        </TableStyle>
      )}
      {isLoading && (
        <LoadingContainer>
          <SpinnerIconLoader iconComponent={<IconComponent name={'loading'} />} size={75} velocity={2} />
        </LoadingContainer>
      )}
      {((!isLoading && data.length < 1) || errorAPI) && (
        <ErrorStyle>
          <span data-testid={'table-error'}>{<DataNotFound text={errorMessage} onRefresh={onRefresh} />}</span>
        </ErrorStyle>
      )}
    </TableContainerStyle>
  )
}

export { Table }
